import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import {
  CurrencyFormDto,
  CurrencyFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/currency/currency-form.dto';
import { BasisCurrencyService } from '@zaoblako/web/core/services/basis/basis-currency.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-currency-form',
  templateUrl: './currency-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    InputNumberModule,
    FormsModule,
    JoinPipe,
    ButtonComponent,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrencyFormComponent extends BaseFormComponent<CurrencyFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public currency = <CurrencyFormInterface>{};
  public loading$: Observable<boolean>;

  public dto = CurrencyFormDto;
  public form$: Observable<FormType>;

  public constructor(
    public readonly formStoreService: FormStoreService,
    private readonly basisCurrencyService: BasisCurrencyService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.basisCurrencyService
      .findById<CurrencyFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, {
        withDeleted: true
      })
      .subscribe({
        next: (currency) => {
          this.currency = currency;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveCurrency(): void {
    if (this.checkValidate(this.currency).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisCurrencyService.update<CurrencyFormInterface, CurrencyFindInterface>(this.currency).subscribe({
          next: (currency) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Валюта "${this.currency.name}" обновлена!`);
            this.formStoreService.saveNext(currency, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisCurrencyService.create<CurrencyFindInterface>(this.currency).subscribe({
          next: (currency) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Валюта создана!');
            this.formStoreService.saveNext(currency, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteCurrency(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить валюту "${this.currency.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisCurrencyService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Валюта "${this.currency.name}" удалена!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }
}
